import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWalletBalanceApi, useAllUnpaidInvoicesApi } from '@hero/api-hooks';
import { usePaymentCheckoutStore } from '@hero/hooks-shared';
import { useFeature } from '@hero/web-context';
import type { BalanceCardProps } from '../BalanceCard/BalanceCard';
import { BalanceCard } from '../BalanceCard/BalanceCard';
import { InvoiceTableManager } from '../../Invoice/Table/InvoiceTableManager/InvoiceTableManager';
import { InvoiceDrawer } from '../../Invoice/Drawer/InvoiceDrawer';
import { OrderDrawer } from '../../Order/Drawer/OrderDrawer';
import { TransactionTableManager } from '../../Transaction/Manager/TransactionTableManager';
import { TransactionDrawer } from '../../Transaction/Drawer/TransactionDrawer';

export interface WalletPageProps {
  displayForUser: 'customer' | 'carrier';
  showDaysLeft?: boolean;
}

/**
 * Customer-only balance section.
 * Calls customer-only hooks (useAllUnpaidInvoicesApi) — safe because this
 * component only mounts when displayForUser === 'customer'.
 */
function CustomerBalanceSection({
  balance,
  currency,
  isLoading,
}: Pick<BalanceCardProps, 'balance' | 'currency' | 'isLoading'>) {
  const { t } = useTranslation('m-payment');
  const openPayment = usePaymentCheckoutStore(s => s.open);
  const invoiceModule = useFeature('invoice_module');
  const subCreateInvoice = useFeature('invoice_module', 'sub_create_invoice');
  const canPayInvoices = invoiceModule.isEnabled && subCreateInvoice.isEnabled;

  const { data: unpaidInvoices = [] } = useAllUnpaidInvoicesApi();
  const hasPayableInvoices = unpaidInvoices.length > 0;

  const handlePayNow = useCallback(() => {
    openPayment({ mode: 'select' });
  }, [openPayment]);

  const handleAddFunds = useCallback(() => {
    openPayment({ mode: 'advance' });
  }, [openPayment]);

  return (
    <BalanceCard
      balance={balance}
      currency={currency}
      isLoading={isLoading}
      actionLabel={canPayInvoices && hasPayableInvoices ? t('actions.pay_now') : undefined}
      onActionClick={canPayInvoices && hasPayableInvoices ? handlePayNow : undefined}
      onAddFunds={handleAddFunds}
      showPayEarlyBanner
    />
  );
}

export function WalletPage({ displayForUser, showDaysLeft = false }: WalletPageProps) {
  const { t } = useTranslation('m-payment');
  const isCustomer = displayForUser === 'customer';
  const invoiceModule = useFeature('invoice_module');

  const { data: balanceData, isLoading: isBalanceLoading } = useWalletBalanceApi();

  const [activeTab, setActiveTab] = useState('invoices');

  const tabs = useMemo(() => {
    const payments = { id: 'payments' as const, label: t('wallet.tabs.payments') };
    if (!invoiceModule.isEnabled) {
      return [payments];
    }
    return [{ id: 'invoices' as const, label: t('wallet.tabs.invoices') }, payments];
  }, [invoiceModule.isEnabled, t]);

  useEffect(() => {
    if (!invoiceModule.isEnabled && activeTab === 'invoices') {
      setActiveTab('payments');
    }
  }, [invoiceModule.isEnabled, activeTab]);

  const activeTabContent =
    activeTab === 'invoices' && invoiceModule.isEnabled ? (
      <InvoiceTableManager
        displayForUser={displayForUser}
        showDaysLeft={showDaysLeft}
        includeActions
      />
    ) : (
      <TransactionTableManager displayForUser={displayForUser} />
    );

  return (
    <>
      <div className="flex flex-col gap-6">
        {isCustomer ? (
          <CustomerBalanceSection
            balance={balanceData?.balance}
            currency={balanceData?.currency}
            isLoading={isBalanceLoading}
          />
        ) : (
          <BalanceCard
            balance={balanceData?.balance}
            currency={balanceData?.currency}
            isLoading={isBalanceLoading}
          />
        )}

        <div className="flex flex-col">
          <div className="flex border-b border-gray-200">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`
                  relative px-6 py-3 text-sm font-medium transition-colors
                  ${
                    activeTab === tab.id
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-gray-500 hover:text-gray-700'
                  }
                `}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mt-4">{activeTabContent}</div>
        </div>
      </div>

      {invoiceModule.isEnabled ? (
        <InvoiceDrawer displayForUser={displayForUser} syncUrl="invoiceId" />
      ) : null}
      <OrderDrawer displayForUser={displayForUser} />
      <TransactionDrawer displayForUser={displayForUser} />
    </>
  );
}
